import { defineStore } from 'pinia'
import apiClient from '@/api/axios'

export const useLanguageStore = defineStore('language', {
  state: () => ({
    // Auth
    isAuthenticated: !!localStorage.getItem('auth_token'),

    // Data
    examTypes: [],
    exams: [],
    exercices: [],
    questions: [],
    currentExercice: null,
    currentQuestions: [],
    currentAttempt: null,
    userAnswers: [],
    practiceType: null,
    currentTrainingQuestion: null,

    // UI State
    loading: false,
    error: null,
    currentView: 'dashboard',
    stats: {
      totalAttempts: 0,
      averageScore: 0,
      completedExercices: 0,
    },
  }),

  getters: {
    getExercicesByType: (state) => (type) => {
      return state.exercices.filter((ex) => ex.exam && ex.exam.type === type)
    },

    getCurrentProgress: (state) => {
      if (!state.currentQuestions.length) return 0
      return Math.round((state.userAnswers.length / state.currentQuestions.length) * 100)
    },

    getScoreByDifficulty: (state) => (difficulty) => {
      return state.stats.averageScore
    },
  },

  actions: {
    changePracticeType(type) {
      this.practiceType = type
    },

    async fetchExamTypes() {
      try {
        this.loading = true
        const response = await apiClient.get('/exam-type/')
        this.examTypes = response.data
      } catch (error) {
        this.error = "Erreur lors du chargement des types d'examen"
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async fetchExams() {
      try {
        this.loading = true
        const response = await apiClient.get('/exam/')
        this.exams = response.data
      } catch (error) {
        this.error = 'Erreur lors du chargement des examens'
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async fetchExercices() {
      try {
        this.loading = true
        const response = await apiClient.get('/exercice/')
        this.exercices = response.data
      } catch (error) {
        this.error = 'Erreur lors du chargement des exercices'
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    async fetchExercicesByExam(examSlug) {
      try {
        // this.loading = true
        const response = await apiClient.get(`/exercice/exam/${examSlug}/`)
        this.exercices = response.data.sort((a, b) => {
          const numA = parseInt(a.title.match(/\d+$/)?.[0] || 0)
          const numB = parseInt(b.title.match(/\d+$/)?.[0] || 0)
          return numA - numB
        })
      } catch (error) {
        this.error = 'Erreur lors du chargement des exercices'
        console.error(error)
        return []
      } finally {
        // this.loading = false
      }
    },

    async fetchQuestionsByExercice(exerciceSlug) {
      try {
        this.loading = true
        const response = await apiClient.get(`/question/exo/${exerciceSlug}/`)
        this.currentQuestions = response.data
        return response.data
      } catch (error) {
        this.error = 'Erreur lors du chargement des questions'
        return []
      } finally {
        this.loading = false
      }
    },

    async startExercice(exerciceSlug) {
      try {
        this.loading = true

        // Récupérer l'exercice
        const exerciceResponse = await apiClient.get(`/exercice/${exerciceSlug}/`)
        this.currentExercice = exerciceResponse.data

        // Créer une tentative
        const attemptResponse = await apiClient.post(`/exercice-attempt/post/${exerciceSlug}/`, {
          started_at: new Date().toISOString(),
          score: 0,
        })
        this.currentAttempt = attemptResponse.data

        // Récupérer les questions
        await this.fetchQuestionsByExercice(exerciceSlug)

        // Réinitialiser les réponses
        this.userAnswers = []
        this.currentView = 'exercice'

        return { success: true }
      } catch (error) {
        this.error = "Erreur lors du démarrage de l'exercice"
        console.error(error)
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve(reader.result)
        }
        reader.onerror = (error) => {
          reject(error)
        }
        reader.readAsDataURL(blob)
      })
    },

    async submitAnswer(questionSlug, answer_data, type = 'READING', audio_file = null) {
      try {
        if (!this.currentAttempt) return { success: false, error: 'Aucune tentative active' }
        let data = null
        if (type === 'WRITING') {
          data = { question_slug: questionSlug, answer_text: answer_data }
        } else if (type === 'SPEAKING') {
          const audio_data = await this.blobToBase64(audio_file)
          data = { question_slug: questionSlug, audio_file: audio_data }
        } else {
          data = { choice: answer_data }
        }
        const response = await apiClient.post(`/answer/post/${this.currentAttempt.slug}/`, data, {
          timeout: 60000,
        })

        // Ajouter la réponse aux réponses utilisateur
        this.userAnswers.push({
          questionSlug,
          answer_data,
          isCorrect: response.data.is_correct,
          answer: response.data,
        })

        return { success: true, data: response.data }
      } catch (error) {
        console.error('Erreur lors de la soumission de la réponse:', error)
        return { success: false, error: 'Erreur lors de la soumission' }
      }
    },

    async completeExercice() {
      try {
        if (!this.currentAttempt) return { success: false }

        // Mettre à jour la tentative
        const response = await apiClient.put(`/exercice-attempt/${this.currentAttempt.slug}/`, {
          completed_at: new Date().toISOString(),
        })

        this.currentAttempt = response.data
      } catch (error) {
        console.error('Erreur lors de la finalisation:', error)
        return { success: false, error: 'Erreur lors de la finalisation' }
      }
    },

    async fetchUserStats() {
      try {
        const response = await apiClient.get('/user/stat')
        const data = response.data

        this.stats = {
          totalAttempts: data?.nb_exercice_attemp,
          averageScore: data?.avg_score,
          completedExercices: data?.nb_exercice_attemp_completed,
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error)
      }
    },

    async fetchAttempStats(attemp_slug) {
      try {
        const response = await apiClient.get(`/exercice-attempt/${attemp_slug}`)
        const attempt = response.data
        this.userAnswers = attempt?.exercice?.questions
        return attempt
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    },

    async getAllExerciceAttempts() {
      try {
        const response = await apiClient.get(`/exercice-attempt/`)
        const attempts = response.data
        return attempts
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    },

    async getRandomQuestion(exam_slug) {
      try {
        const response = await apiClient.get(`/question/aleat/${exam_slug}/`)
        this.currentTrainingQuestion = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    },

    async submitVoiceAnswer(questionSlug, audioBlob) {
      try {
        if (!this.currentAttempt) return { success: false, error: 'Aucune tentative active' }

        // Créer un FormData pour envoyer le fichier audio
        const formData = new FormData()
        formData.append('question_slug', questionSlug)
        formData.append('audio_file', audioBlob, `recording_${questionSlug}.wav`)
        formData.append('answer_type', 'VOICE')

        const response = await apiClient.post(
          `/answer/post/${this.currentAttempt.slug}/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 60000,
          },
        )

        // Ajouter la réponse aux réponses utilisateur
        this.userAnswers.push({
          questionSlug,
          answer_data: 'voice_recording',
          isCorrect: response.data.is_correct,
          answer: response.data,
          type: 'voice',
        })

        return { success: true, data: response.data }
      } catch (error) {
        console.error('Erreur lors de la soumission de la réponse vocale:', error)
        return {
          success: false,
          error: error.response?.data?.message || 'Erreur lors de la soumission vocale',
        }
      }
    },

    // Méthode pour vérifier les permissions audio
    async checkAudioPermissions() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach((track) => track.stop()) // Arrêter immédiatement
        return { success: true }
      } catch (error) {
        console.error('Permissions audio refusées:', error)
        return {
          success: false,
          error: 'Permissions microphone requises pour cet exercice',
        }
      }
    },

    async correctAnswer(slug, answer_slug) {
      try {
        let response = await apiClient.post(
          `/answer/make-new-correction/`,
          {user_aswer_slug: answer_slug}
        )
        this.userAnswers = this.userAnswers.map((ans) => {
          if (ans.user_aswer_slug === answer_slug) {
            ans.ai_aswer = response.data.recommendations
            return ans
          }
          return ans
        })
      } catch (error) {
        throw error
      }
    },

    // UI Actions
    setCurrentView(view) {
      this.currentView = view
    },

    resetCurrentExercice() {
      this.currentExercice = null
      this.currentQuestions = []
      this.currentAttempt = null
      this.userAnswers = []
      this.currentView = 'dashboard'
    },

    clearError() {
      this.error = null
    },
  },
})
